import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User, Project } from '../../types';
import { projectsAPI, usersAPI, companiesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Company {
  _id: string;
  name: string;
  domain?: string;
  logo?: string;
  email?: string;
  phone?: string;
  address?: any;
  contactPerson?: string;
  contactEmail?: string;
  contactPhone?: string;
  website?: string;
  industry?: string;
  size?: string;
  users?: any[];
  projects?: any[];
  subscription?: {
    plan: string;
    status?: string;
    maxProjects?: number;
    maxUsers?: number;
  };
  settings?: {
    branding?: { primaryColor?: string; secondaryColor?: string };
    features?: Record<string, boolean>;
  };
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface AdminStats {
  totalUsers: number;
  totalProjects: number;
  totalCompanies: number;
  activeProjects: number;
}

interface AdminContextType {
  companies: Company[];
  projects: any[];
  users: any[];
  stats: AdminStats;
  loading: boolean;
  refreshData: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | null>(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within AdminProvider');
  }
  return context;
};

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSuperAdmin } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalProjects: 0,
    totalCompanies: 0,
    activeProjects: 0,
  });
  const [loading, setLoading] = useState(true);

  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      const fetches: [Promise<any[]>, Promise<any[]>, Promise<any[]>] = [
        projectsAPI.getAll().catch(() => []),
        usersAPI.getAll().catch(() => []),
        // Beheerders zonder superAdmin krijgen de klantenlijst voor de keuzelijst "Klant" (alleen naam/logo).
        isSuperAdmin ? companiesAPI.getAll().catch(() => []) : companiesAPI.options().catch(() => []),
      ];

      const [projectsData, usersData, companiesData] = await Promise.all(fetches);

      setProjects(projectsData);
      setUsers(usersData);
      setCompanies(companiesData);

      setStats({
        totalUsers: usersData.length,
        totalProjects: projectsData.length,
        totalCompanies: companiesData.length,
        activeProjects: projectsData.filter((p: any) => p.status === 'active').length,
      });
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return (
    <AdminContext.Provider value={{ companies, projects, users, stats, loading, refreshData }}>
      {children}
    </AdminContext.Provider>
  );
};

export type { Company, AdminStats };
